import type { ArtifactListingEntry } from '@crowdex/agent-contracts/api';
import { lastPanelTabOf, type PanelTabs, panelTabsOf } from '@/lib/side-panel/panel-tabs';
import type { SidePanelItem, SidePanelView } from '@/lib/side-panel/side-panel-items';

/** An open tab of the side panel as its tab strip and the summary show it. */
export type PanelInstance = {
  id: string;
  type: SidePanelView;
  title: string;
  /** The tab its view shows (the last one shown of a view open more than once). */
  selected?: boolean;
  status?: 'running';
  variant?: 'default' | 'background';
  /** A per-instance icon image (a browser tab's favicon) replacing the panel icon. */
  iconUrl?: string;
  /** Stands for every open tab of its view (the browser's pages, listed in the sidebar). */
  grouped?: true;
};

/** What a tab of a view open more than once shows: its browser page, its terminal. */
export type PanelTabSession = { name: string; favicon?: string };

/**
 * Describes the open tabs in strip order. A tab of a view open more than once is named by its
 * session, and left out until it has one; another tab, by its session when it has one (an app's
 * tab, by its item).
 */
export function describePanelTabs({
  tabs,
  items,
  artifacts = [],
  sessions,
}: {
  tabs: PanelTabs;
  items: readonly SidePanelItem[];
  artifacts?: readonly ArtifactListingEntry[];
  sessions: ReadonlyMap<string, PanelTabSession>;
}): PanelInstance[] {
  return tabs.tabs.flatMap((tab): PanelInstance[] => {
    if (tab.view.startsWith('artifact:')) {
      const artifact = artifacts.find((item) => `artifact:${item.id}` === tab.view);
      return [{ id: tab.id, type: tab.view, title: artifact?.title ?? 'Result' }];
    }
    const item = items.find((candidate) => candidate.id === tab.view);
    if (item === undefined) return [];
    const session = sessions.get(tab.id);
    // An app's tab is named by the item it shows, once it shows one; of several, the one shown
    // last is marked as the view's.
    if (item.multiple !== true)
      return [
        {
          id: tab.id,
          type: tab.view,
          title: session?.name ?? item.label,
          ...(panelTabsOf(tabs, tab.view).length > 1
            ? { selected: lastPanelTabOf(tabs, tab.view)?.id === tab.id }
            : {}),
        },
      ];
    if (session === undefined) return [];
    return [
      {
        id: tab.id,
        type: tab.view,
        title: session.name,
        selected: lastPanelTabOf(tabs, tab.view)?.id === tab.id,
        ...(session.favicon === undefined ? {} : { iconUrl: session.favicon }),
      },
    ];
  });
}

/**
 * The strip with the tabs of `view` as one tab, where the first was: the browser on the sidebar's
 * side lists its pages there, so the strip shows it once, as the app it is. It stands for the
 * page shown last.
 */
export function groupViewTabs(
  instances: readonly PanelInstance[],
  view: SidePanelView,
  title: string,
): PanelInstance[] {
  const members = instances.filter((instance) => instance.type === view);
  if (members.length === 0) return [...instances];
  const shown = members.find((instance) => instance.selected) ?? members[0];
  const first = instances.indexOf(members[0] as PanelInstance);
  return instances.flatMap((instance, index) =>
    instance.type !== view
      ? [instance]
      : index === first
        ? [{ id: shown?.id ?? instance.id, type: view, title, selected: true, grouped: true }]
        : [],
  );
}
